/**
 * TIPOS DE JUNTA ENSAIADA — Ultrassom Convencional.
 *
 * Os quatro modelos gráficos do FORM-USC (seção 04). A geometria de cada um
 * vive AQUI, em coordenadas, e alimenta a página, o Preview e o PDF pelo
 * mesmo SVG — nunca uma imagem recortada, nunca uma figura gerada por IA.
 * Os números 1..6 são posições fixas da geometria: "TIPO 1 / PONTO 3" é
 * sempre o mesmo ponto gráfico. A lista `inspectionPoints` é derivada das
 * etiquetas do próprio desenho — não existe lista manual.
 *
 * Nomenclatura: enquanto não houver nome técnico oficial no cadastro, os
 * modelos chamam-se TIPO 1..4 (a geometria é a identificação).
 *
 * `USC_JOINT_DEFINITION_VERSION` sobe a cada mudança de geometria; o relatório
 * congela a versão que usou, e um documento assinado nunca muda.
 */

export const USC_JOINT_DEFINITION_VERSION = 1;

export type UsJointTypeId = "TIPO_1" | "TIPO_2" | "TIPO_3" | "TIPO_4";

export const US_JOINT_TYPE_IDS: readonly UsJointTypeId[] = ["TIPO_1", "TIPO_2", "TIPO_3", "TIPO_4"];

export type Point = readonly [number, number];

export interface UsJointPath {
  points: readonly Point[];
  closed?: boolean;
}

export interface UsJointLabel {
  /** Número do ponto de inspeção, exatamente como impresso na figura. */
  n: number;
  /** Centro da etiqueta no espaço do desenho. */
  x: number;
  y: number;
}

export interface UsJointDiagram {
  width: number;
  height: number;
  paths: readonly UsJointPath[];
  labels: readonly UsJointLabel[];
}

export interface UsJointModel {
  id: UsJointTypeId;
  code: string;
  label: string;
  diagram: UsJointDiagram;
  /** Derivado das etiquetas do desenho, em ordem crescente. */
  inspectionPoints: readonly number[];
  version: number;
}

const W = 200;
const H = 120;

/** Marca de interrupção (a peça continua) na extremidade direita de uma chapa entre y0 e y1. */
function breakMark(x: number, y0: number, y1: number): UsJointPath {
  const mid = (y0 + y1) / 2;
  const q = (y1 - y0) / 4;
  return { points: [[x, y0], [x - 5, y0 + q], [x + 5, mid], [x - 5, y1 - q], [x, y1]] };
}

/**
 * TIPO 1 — junta de topo com chanfro em V (chapa horizontal, V da face
 * superior até a raiz). Pontos: 1 · 3 · 2 na face superior, 4 · 6 · 5 na face
 * inferior (3 e 6 sobre a solda).
 */
const TIPO_1: UsJointDiagram = {
  width: W,
  height: H,
  paths: [
    { points: [[20, 45], [180, 45], [180, 85], [20, 85]], closed: true },
    { points: [[86, 45], [100, 85], [114, 45]] }
  ],
  labels: [
    { n: 1, x: 40, y: 33 }, { n: 3, x: 100, y: 33 }, { n: 2, x: 160, y: 33 },
    { n: 4, x: 40, y: 100 }, { n: 6, x: 100, y: 100 }, { n: 5, x: 160, y: 100 }
  ]
};

/**
 * TIPO 2 — junta em T: chapa base horizontal e alma vertical com chanfro em
 * V na raiz. Pontos: 4 · 1 | alma | 2 · 5 sobre a face superior da base,
 * 3 na face inferior sob a solda.
 */
const TIPO_2: UsJointDiagram = {
  width: W,
  height: H,
  paths: [
    { points: [[20, 75], [180, 75], [180, 105], [20, 105]], closed: true },
    { points: [[88, 75], [88, 15], [112, 15], [112, 75]] },
    { points: [[88, 75], [100, 88], [112, 75]] }
  ],
  labels: [
    { n: 4, x: 52, y: 66 }, { n: 1, x: 78, y: 66 }, { n: 2, x: 122, y: 66 }, { n: 5, x: 148, y: 66 },
    { n: 3, x: 100, y: 114 }
  ]
};

/**
 * TIPO 3 — junta em T / canto com chanfro duplo (K): chapa vertical à
 * esquerda, chapa horizontal chegando pela direita com a extremidade
 * chanfrada nas duas faces (vértice no meio da espessura). Pontos: 1 na face
 * superior da chapa horizontal, 2 na inferior, 3 na face oposta da vertical.
 */
const TIPO_3: UsJointDiagram = {
  width: W,
  height: H,
  paths: [
    { points: [[30, 18], [50, 18], [50, 110], [30, 110]], closed: true },
    { points: [[170, 45], [82, 45], [50, 62]] },
    { points: [[170, 79], [82, 79], [50, 62]] },
    breakMark(170, 45, 79)
  ],
  labels: [
    { n: 1, x: 126, y: 34 }, { n: 2, x: 126, y: 92 }, { n: 3, x: 16, y: 62 }
  ]
};

/**
 * TIPO 4 — junta em T / canto com chanfro simples (meio V): como o TIPO 3,
 * mas só a face superior é chanfrada — a inferior encosta reta na vertical.
 * Pontos: 1 face superior, 2 face inferior, 3 face oposta da vertical.
 */
const TIPO_4: UsJointDiagram = {
  width: W,
  height: H,
  paths: [
    { points: [[30, 18], [50, 18], [50, 110], [30, 110]], closed: true },
    { points: [[170, 45], [82, 45], [50, 79]] },
    { points: [[170, 79], [50, 79]] },
    breakMark(170, 45, 79)
  ],
  labels: [
    { n: 1, x: 126, y: 34 }, { n: 2, x: 126, y: 92 }, { n: 3, x: 16, y: 62 }
  ]
};

function model(id: UsJointTypeId, n: number, diagram: UsJointDiagram): UsJointModel {
  return {
    id,
    code: `TIPO ${n}`,
    label: `TIPO ${n}`,
    diagram,
    inspectionPoints: [...diagram.labels].map(l => l.n).sort((a, b) => a - b),
    version: USC_JOINT_DEFINITION_VERSION
  };
}

export const US_JOINT_MODELS: readonly UsJointModel[] = [
  model("TIPO_1", 1, TIPO_1),
  model("TIPO_2", 2, TIPO_2),
  model("TIPO_3", 3, TIPO_3),
  model("TIPO_4", 4, TIPO_4)
];

export function usJointById(id: string | null | undefined): UsJointModel | null {
  return US_JOINT_MODELS.find(m => m.id === id) ?? null;
}

export function usJointLabel(id: string | null | undefined): string {
  return usJointById(id)?.label ?? (id ? String(id).replace("_", " ") : "");
}

/** Os pontos que existem NA FIGURA do tipo — a única fonte do select "Ponto de Inspeção". */
export function inspectionPointsOf(id: string | null | undefined): readonly number[] {
  return usJointById(id)?.inspectionPoints ?? [];
}

export function isValidInspectionPoint(id: string | null | undefined, point: number | string | null | undefined): boolean {
  if (point === null || point === undefined || point === "") return false;
  const n = typeof point === "number" ? point : Number(String(point).trim());
  return Number.isInteger(n) && inspectionPointsOf(id).includes(n);
}

// ------------------------------------------------------------------ DIGITS

/**
 * Algarismos 1..6 como traços (caixa 6 × 10) — os números da figura são
 * vetoriais como o resto do desenho, sem depender de fonte no PDF nem no
 * navegador. Legíveis em impressão preto e branco.
 */
const DIGIT_STROKES: Record<number, readonly (readonly Point[])[]> = {
  1: [[[1, 2], [3, 0], [3, 10]], [[1, 10], [5, 10]]],
  2: [[[0, 1.5], [1.5, 0], [4.5, 0], [6, 1.5], [6, 3.5], [0, 10], [6, 10]]],
  3: [[[0, 0], [6, 0], [2.5, 4.2], [4.5, 4.2], [6, 5.7], [6, 8.5], [4.5, 10], [1.5, 10], [0, 8.5]]],
  4: [[[4.5, 10], [4.5, 0], [0, 6.8], [6, 6.8]]],
  5: [[[6, 0], [0, 0], [0, 4.5], [4.2, 4.5], [6, 6], [6, 8.5], [4.5, 10], [1.5, 10], [0, 8.5]]],
  6: [[[5.5, 0], [1.8, 0], [0, 2], [0, 8.5], [1.5, 10], [4.5, 10], [6, 8.5], [6, 6.5], [4.5, 5], [0, 5]]]
};

/** Traços do algarismo centrados em (cx, cy), com altura `size`. */
export function digitStrokes(n: number, cx: number, cy: number, size = 11): UsJointPath[] {
  const strokes = DIGIT_STROKES[n];
  if (!strokes) return [];
  const s = size / 10;
  const w = 6 * s;
  return strokes.map(seg => ({ points: seg.map(([x, y]) => [cx - w / 2 + x * s, cy - size / 2 + y * s] as Point) }));
}

// --------------------------------------------------------------------- SVG

export interface UsJointSvgOptions {
  /** Cor dos traços (a página escura usa branco; o PDF usa preto). */
  stroke?: string;
  strokeWidth?: number;
  /** Largura final em px/pt; a altura segue a proporção do desenho. */
  width?: number;
  /** Fundo (ex.: "#FFFFFF" no PDF). Sem fundo por padrão. */
  background?: string | null;
}

function fmt(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
}

function polyline(path: UsJointPath, stroke: string, strokeWidth: number): string {
  const pts = path.points.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(" ");
  const tag = path.closed ? "polygon" : "polyline";
  return `<${tag} points="${pts}" fill="none" stroke="${stroke}" stroke-width="${fmt(strokeWidth)}" stroke-linejoin="round" stroke-linecap="round"/>`;
}

/**
 * O SVG do modelo — o MESMO para a página, o Preview e o PDF (pdfmake
 * renderiza SVG vetorialmente). Só polilinhas: nada aqui depende de fonte.
 */
export function usJointSvg(id: UsJointTypeId | string, options: UsJointSvgOptions = {}): string {
  const m = usJointById(id);
  if (!m) return "";
  const stroke = options.stroke ?? "#000000";
  const strokeWidth = options.strokeWidth ?? 2;
  const { width: vw, height: vh } = m.diagram;
  const width = options.width ?? vw;
  const height = Math.round((width * vh) / vw);
  const parts: string[] = [];
  if (options.background) parts.push(`<rect x="0" y="0" width="${vw}" height="${vh}" fill="${options.background}"/>`);
  for (const path of m.diagram.paths) parts.push(polyline(path, stroke, strokeWidth));
  for (const label of m.diagram.labels) {
    for (const seg of digitStrokes(label.n, label.x, label.y)) parts.push(polyline(seg, stroke, strokeWidth * 0.9));
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vw} ${vh}" width="${width}" height="${height}" role="img" aria-label="${m.label}">${parts.join("")}</svg>`;
}

/** Assinatura estável da geometria — o snapshot do relatório guarda e o teste compara. */
export function usJointGeometryFingerprint(id: UsJointTypeId | string): string {
  const m = usJointById(id);
  if (!m) return "";
  return JSON.stringify({ v: m.version, paths: m.diagram.paths, labels: m.diagram.labels });
}
